import { useState } from 'react';
import { administrativeAddPeriod } from '@snaphire/contracts';
import { Field, Icon, IconButton, Table, Tabs } from '../ui';
import type { Schema } from '../api/client';
import {
  AdminLink,
  QueryState,
  dateLabel,
  kindLabels,
  moneyLabel,
  today,
  useAdministrative,
  useAdminQuery,
} from './common';
import { AdministrativeFilters } from './list-layout';
import { Dropdown } from '../dropdown';

export function PeriodFilter({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  const me = useAdministrative();
  const [mode, setMode] = useState('month');
  return (
    <div className="admin-period">
      <Tabs
        tabs={[
          { value: 'week', label: 'Неделя' },
          { value: 'month', label: 'Месяц' },
          { value: 'custom', label: 'Период' },
        ]}
        value={mode}
        onChange={(value) => {
          setMode(value);
          if (value !== 'custom') {
            const start = today(me);
            onChange(
              start,
              administrativeAddPeriod(start, {
                count: value === 'week' ? 6 : 1,
                unit: value === 'week' ? 'DAY' : 'MONTH',
              }),
            );
          }
        }}
      />
      <Field label="С" type="date" value={from} onChange={(e) => onChange(e.target.value, to)} />
      <Field
        label="По"
        aria-label="Конец периода"
        type="date"
        min={from}
        value={to}
        onChange={(e) => onChange(from, e.target.value)}
      />
    </div>
  );
}
export function AdministrativeOverview() {
  const me = useAdministrative();
  const [range, setRange] = useState({
    from: today(me),
    to: administrativeAddPeriod(today(me), { count: 1, unit: 'MONTH' }),
  });
  const [filters, setFilters] = useState<Record<string, string>>({});
  const selectedFilters = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
  const query = useAdminQuery<Schema<'AdminDashboard'>>(
    '/api/v1/administrative/dashboard',
    {},
    { ...range, ...selectedFilters },
  );
  return (
    <>
      <section className="panel admin-overview-controls">
        <div className="table-toolbar">
          <PeriodFilter {...range} onChange={(from, to) => setRange({ from, to })} />
          <AdministrativeFilters
            values={filters}
            onApply={setFilters}
            fields={[
              { key: 'projectId', label: 'Проект', allLabel: 'Все проекты', options: me.projects },
              {
                key: 'providerAccountId',
                label: 'Учётная запись',
                allLabel: 'Все провайдеры',
                options: me.providers,
              },
              {
                key: 'kind',
                label: 'Тип ресурса',
                allLabel: 'Все типы',
                options: Object.entries(kindLabels).map(([id, name]) => ({ id, name })),
              },
              ...(me.allowedActions.includes('finance.read')
                ? [
                    {
                      key: 'currency',
                      label: 'Валюта',
                      allLabel: 'Все валюты',
                      options: ['USD', 'EUR', 'RUB'].map((id) => ({ id, name: id })),
                    },
                  ]
                : []),
            ]}
          />
          <span className="toolbar-spacer" />
          {me.allowedActions.includes('section.create') && (
            <AdminLink path="sections/new" className="pill">
              <Icon name="Plus" size={16} />
              Создать раздел
            </AdminLink>
          )}
        </div>
      </section>
      <QueryState query={query} />
      {query.data && (
        <>
          <div className="admin-metrics">
            {query.data.counts.map((count) => (
              <section className="panel admin-summary-card" key={count.kind}>
                <h2>{kindLabels[count.kind]}</h2>
                <div className="admin-big-number">
                  {count.relevant + count.irrelevant + count.unknownRelevance}
                </div>
                <div className="admin-summary-status">
                  <p>
                    {count.kind === 'PROXY'
                      ? `С действующим сроком: ${count.valid}`
                      : `Актуальные: ${count.relevant} · Неактуальные: ${count.irrelevant}`}
                  </p>
                  <p className="muted">
                    Просроченные: {count.expired} · Без срока: {count.unknownExpiry}
                  </p>
                  {count.kind === 'PROXY' && count.available !== undefined && (
                    <p className="muted">
                      Доступны: {count.available} · Недоступны: {count.unavailable} · Без результата
                      проверки: {count.unknownAvailability}
                    </p>
                  )}
                </div>
                <AdminLink
                  path={`resources?${new URLSearchParams({ ...selectedFilters, kind: count.kind })}`}
                >
                  Открыть ресурсы
                </AdminLink>
              </section>
            ))}
          </div>
          {query.data.financialTotalsAvailable && (
            <div className="admin-metrics">
              {(
                [
                  ['planned', 'Плановые платежи'],
                  ['actual', 'Фактические оплаты'],
                  ['overdue', 'Просроченные сроки'],
                  ['nonRelevant', 'Неактуальные ресурсы'],
                ] as const
              ).map(([key, title]) => (
                <section className="panel" key={key}>
                  <h2>{title}</h2>
                  {Object.entries(query.data?.[key] || {}).map(([currency, amount]) => (
                    <p className="admin-total" key={currency}>
                      {moneyLabel(amount, currency)}
                    </p>
                  ))}
                </section>
              ))}
            </div>
          )}
          {query.data.unknownPrices ? (
            <p className="muted">Ресурсов с неизвестной стоимостью: {query.data.unknownPrices}</p>
          ) : null}
          <section className="panel">
            <h2>Разделы</h2>
            <div className="admin-card-list">
              {me.sections.map((section) => (
                <AdminLink
                  key={section.id}
                  path={`sections/${section.id}`}
                  className="admin-card-link"
                >
                  <strong>{section.name}</strong>
                  <span className="muted">
                    {section.template === 'TECHNICAL' ? 'Технический раздел' : 'Прокси'}
                  </span>
                </AdminLink>
              ))}
            </div>
            {!me.sections.length && (
              <p className="muted">
                Создайте раздел из технического или прокси-шаблона, чтобы добавить ресурсы.
              </p>
            )}
          </section>
        </>
      )}
    </>
  );
}
export function AdministrativeCalendar() {
  const me = useAdministrative();
  const [month, setMonth] = useState(today(me).slice(0, 7));
  const [view, setView] = useState('month');
  const [projectId, setProject] = useState('');
  const [providerAccountId, setProvider] = useState('');
  const [kind, setKind] = useState('');
  const from = `${month}-01`;
  const nextMonth = administrativeAddPeriod(from, { count: 1, unit: 'MONTH' });
  const to = new Date(new Date(nextMonth + 'T12:00:00Z').getTime() - 86_400_000)
    .toISOString()
    .slice(0, 10);
  const query = useAdminQuery<Schema<'AdminCalendar'>>(
    '/api/v1/administrative/calendar',
    {},
    {
      from,
      to,
      ...(projectId ? { projectId } : {}),
      ...(providerAccountId ? { providerAccountId } : {}),
      ...(kind ? { kind } : {}),
    },
  );
  const days = Number(to.slice(-2));
  const monthName = (value: string) =>
    new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
      new Date(value + '-01T12:00:00Z'),
    );
  const events = query.data?.items || [];
  const eventDescription = (entry: Schema<'AdminCalendar'>['items'][number]) =>
    [
      entry.name,
      kindLabels[entry.kind],
      `Проект: ${me.projects.find((item) => item.id === entry.projectId)?.name || (entry.projectId ? 'Недоступен' : 'Не указан')}`,
      `Учётная запись: ${me.providers.find((item) => item.id === entry.providerAccountId)?.name || (entry.providerAccountId ? 'Недоступна' : 'Не указана')}`,
      dateLabel(entry.date),
      ...('amount' in entry ? [`План: ${moneyLabel(entry.amount, entry.currency)}`] : []),
    ].join(' · ');
  const resources = [...new Map(events.map((item) => [item.resourceId, item])).values()];
  const months = Array.from({ length: 84 }, (_, index) => {
    const value = new Date(Date.UTC(Number(month.slice(0, 4)) - 3, index, 1))
      .toISOString()
      .slice(0, 7);
    return { id: value, label: monthName(value) };
  });
  return (
    <section className="panel data-panel calendar-panel admin-list admin-calendar-panel">
      <div className="table-toolbar">
        <div className="tabs admin-calendar-view" role="tablist" aria-label="Вид календаря">
          {[
            { value: 'month', label: 'Календарь', icon: 'Calendar' },
            { value: 'list', label: 'Список', icon: 'DocumentsPair' },
          ].map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-label={tab.label}
              title={tab.label}
              aria-selected={view === tab.value}
              className={view === tab.value ? 'selected' : ''}
              onClick={() => setView(tab.value)}
            >
              <Icon name={tab.icon} size={16} />
            </button>
          ))}
        </div>
        <CalendarFilters
          projectId={projectId}
          setProject={setProject}
          providerAccountId={providerAccountId}
          setProvider={setProvider}
          kind={kind}
          setKind={setKind}
        />
        <span className="toolbar-spacer" />
        <AdminLink path="payments" className="pill">
          История оплат
        </AdminLink>
      </div>
      <div className="calendar-title">
        <IconButton
          icon="ChevronLeft"
          label="Предыдущий месяц"
          onClick={() =>
            setMonth(
              new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)) - 2, 1))
                .toISOString()
                .slice(0, 7),
            )
          }
        />
        <Dropdown
          label="Месяц"
          className="admin-month-select"
          selected={[month]}
          options={months}
          onChange={(values) => {
            if (values[0]) setMonth(values[0]);
          }}
        />
        <IconButton
          icon="ChevronRight"
          label="Следующий месяц"
          onClick={() => setMonth(nextMonth.slice(0, 7))}
        />
      </div>
      <QueryState query={query} />
      {query.data && (
        <>
          {view === 'month' ? (
            <div
              className="calendar-scroll"
              tabIndex={0}
              role="region"
              aria-label="Платежи по дням месяца"
            >
              <table
                className="calendar admin-calendar-matrix"
                style={{ minWidth: `calc(var(--admin-calendar-resource-width) + ${days * 36}px)` }}
              >
                <colgroup>
                  <col style={{ width: 'var(--admin-calendar-resource-width)' }} />
                  {Array.from({ length: days }, (_, index) => (
                    <col key={index} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th>Ресурс · {resources.length}</th>
                    {Array.from({ length: days }, (_, index) => {
                      const date = `${month}-${String(index + 1).padStart(2, '0')}`;
                      return (
                        <th key={date}>
                          <span className={date === today(me) ? 'today' : ''}>{index + 1}</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {resources.map((resource) => (
                    <tr key={resource.resourceId}>
                      <td>
                        <AdminLink
                          className="admin-calendar-resource"
                          path={`resources/${resource.resourceId}`}
                        >
                          <span className="admin-resource-type">
                            <Icon
                              name={resource.kind === 'PROXY' ? 'Controls' : 'DocumentsPair'}
                              size={16}
                            />
                          </span>
                          {resource.name}
                        </AdminLink>
                      </td>
                      {Array.from({ length: days }, (_, index) => {
                        const date = `${month}-${String(index + 1).padStart(2, '0')}`;
                        const entry = events.find(
                          (item) => item.resourceId === resource.resourceId && item.date === date,
                        );
                        return (
                          <td key={date}>
                            {entry ? (
                              <AdminLink
                                path={`resources/${resource.resourceId}`}
                                className="calendar-day admin-payment-day"
                              >
                                <span
                                  title={eventDescription(entry)}
                                  aria-label={eventDescription(entry)}
                                >
                                  ●
                                </span>
                              </AdminLink>
                            ) : (
                              <span className="calendar-day non-working" aria-hidden="true" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!resources.length && (
                <div className="table-empty">Нет плановых платежей в этом месяце</div>
              )}
            </div>
          ) : (
            <Table
              rows={events.map((item) => ({ ...item, id: item.resourceId + item.date }))}
              columns={[
                { key: 'date', label: 'Дата', render: (row) => dateLabel(row.date) },
                {
                  key: 'name',
                  label: 'Ресурс',
                  render: (row) => (
                    <AdminLink path={`resources/${row.resourceId}`}>{row.name}</AdminLink>
                  ),
                },
                { key: 'kind', label: 'Тип', render: (row) => kindLabels[row.kind] },
                {
                  key: 'projectId',
                  label: 'Проект',
                  render: (row) =>
                    me.projects.find((item) => item.id === row.projectId)?.name ||
                    (row.projectId ? 'Недоступен' : 'Не указан'),
                },
                {
                  key: 'providerAccountId',
                  label: 'Учётная запись',
                  render: (row) =>
                    me.providers.find((item) => item.id === row.providerAccountId)?.name ||
                    (row.providerAccountId ? 'Недоступна' : 'Не указана'),
                },
                ...(query.data.financialTotalsAvailable
                  ? [
                      {
                        key: 'amount',
                        label: 'Плановая сумма',
                        render: (row: Schema<'AdminCalendar'>['items'][number]) =>
                          moneyLabel(row.amount, row.currency),
                      },
                    ]
                  : []),
              ]}
            />
          )}
          {query.data.financialTotalsAvailable && (
            <div className="admin-calendar-totals">
              {Object.entries(query.data.totals || {}).map(([currency, amount]) => (
                <span key={currency}>
                  <span className="muted">План за месяц · </span>
                  {moneyLabel(amount, currency)}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
export function CalendarFilters({
  projectId,
  setProject,
  providerAccountId,
  setProvider,
  kind,
  setKind,
}: {
  projectId: string;
  setProject: (value: string) => void;
  providerAccountId: string;
  setProvider: (value: string) => void;
  kind: string;
  setKind: (value: string) => void;
}) {
  const me = useAdministrative();
  return (
    <AdministrativeFilters
      label="Фильтры календаря"
      values={{ projectId, providerAccountId, kind }}
      fields={[
        { key: 'projectId', label: 'Проект', allLabel: 'Все проекты', options: me.projects },
        {
          key: 'providerAccountId',
          label: 'Провайдер',
          allLabel: 'Все провайдеры',
          options: me.providers,
        },
        {
          key: 'kind',
          label: 'Тип ресурса',
          allLabel: 'Все типы',
          options: Object.entries(kindLabels).map(([id, name]) => ({ id, name })),
        },
      ]}
      onApply={(values) => {
        setProject(values.projectId || '');
        setProvider(values.providerAccountId || '');
        setKind(values.kind || '');
      }}
    />
  );
}
